class Menu {
    private firstOpen: boolean = false; // This will get replaced almost immediately

    private readonly menuOpenButton: HTMLElement|null;
    private readonly menuCloseButton: HTMLElement|null;

    private readonly mobileMenuDialog: HTMLElement|null;
    private readonly mobileMenuHeader: HTMLElement|null;

    constructor(private readonly wrapper: HTMLElement) {
	    this.menuOpenButton = this.wrapper.querySelector<HTMLElement>('.mobile-menu-open-button');
	    this.menuCloseButton = this.wrapper.querySelector<HTMLElement>('.mobile-menu-close-button');
	    this.mobileMenuDialog = this.wrapper.querySelector<HTMLElement>('.mobile-menu-modal-dialog');
	    this.mobileMenuHeader = this.wrapper.querySelector<HTMLElement>('.mobile-menu-header');

        // Setup
        this.setupMenu();

        // AJAX load a new version of the HTML for wrapper
        const container = wrapper.querySelector<HTMLElement>('div[data-plaudit-menu-url]');
		if (!container) {
			return;
		}
        const menuURL = container?.getAttribute('data-plaudit-menu-url');
        if (menuURL) {
            // console.log('Load new menu HTML from:', menuURL);
            fetch(menuURL).then(response => {
                if (response.ok) {
                    response.text().then(text => {
                        container.outerHTML = text;

                        // Loaded menu does not have the current item marked with a class, so we need to do that
                        // by looking for a link with the current URL, and if that doesn't exist then the first link
                        // with the current URL but excluding the fragment identifier
                        // NOTE: When developing locally this will not work on port 3000 since response comes back with 8443
                        const currentURL = window.location.href;
                        let currentLink = this.wrapper.querySelector<HTMLElement>(`a[href="${currentURL}"]`);
                        if (!currentLink) {
                            const currentURLNoFragment = currentURL.split('#')[0];
                            currentLink = this.wrapper.querySelector<HTMLElement>(`a[href="${currentURLNoFragment}"]`);
                        }
                        if (currentLink) {
                            const currentItem = currentLink.closest('li');
                            if (currentItem) {
                                // Current
                                currentItem.classList.add('current-menu-item');

                                let parent = currentItem.parentNode as HTMLElement;
                                let parentFound = false;
                                while (parent && parent !== this.mobileMenuDialog) {
                                    if (parent.classList.contains('sub-menu')) {
                                        if (!parentFound) {
                                            // Parent
                                            parentFound = true;
                                            parent.classList.add('current-menu-parent');
                                        }

                                        // Ancestor
                                        parent.classList.add('current-menu-ancestor');
                                    }
                                    parent = parent.parentNode as HTMLElement;
                                }
                            }
                        }


                        // Treat as new menu
                        this.firstOpen = false;
                        this.openCurrentItemSubmenus();
                    });
                } else {
                    console.error('Failed to load menu HTML');
                }
            });
        }
    }

    private setupMenu() {
        // Open listener
        if (this.menuOpenButton) {
            this.firstOpen = true;
            this.menuOpenButton.addEventListener('click', () => {
                if (this.firstOpen) {
                    // We open current submenus on first open
                    this.firstOpen = false;
                    this.openCurrentItemSubmenus();
                }
                this.toggleMenuDialog(true);
            });
        }

        // Close button click
        if (this.menuCloseButton) {
            this.menuCloseButton.addEventListener('click', () => this.toggleMenuDialog(false));
        }

        // Parent button click, open accordion
	    if (this.mobileMenuDialog) {
		    this.mobileMenuDialog.addEventListener('click', e => {
			    if (e.target instanceof HTMLElement && e.target.classList.contains('mobile-menu-item--button')) {
				    this.toggleSubmenu(e.target as HTMLElement);
			    }
		    });

		    // Trap focus
		    this.mobileMenuDialog.addEventListener('keydown', e => this.trapFocus(e));
	    }
    }

    /**
     * Open submenus up to current item
     */
    openCurrentItemSubmenus() {
        // First the first item marked current.
        let currentItem: HTMLElement|null = null;
        for (const selector of [ '.current-menu-item > ul > .current-menu-item > a',    // First find 'Overview' menu item
                                 '.current-menu-item > :is(button, a)' ]) {             // Then a link or button
            currentItem = this.mobileMenuDialog!.querySelector<HTMLElement>(selector);
            if (currentItem)
                break;
        }

        if (currentItem) {
            // Open all ancestors
            let parent = currentItem.parentNode as HTMLElement;
            while (parent && parent !== this.mobileMenuDialog) {
                if (parent.classList.contains('sub-menu')) {
                    const button = parent.closest('li')?.querySelector('.mobile-menu-item--button') as HTMLElement|null;
                    if (button) {
                        this.toggleSubmenu(button, true);
                    }
                }
                parent = parent.parentNode as HTMLElement;
            }
        }
    }

    toggleMenuDialog(open: boolean|null = null) {
        // Determine if open or close
        open = open ?? !this.mobileMenuDialog?.classList.contains('mobile-menu--open');

        // Open the menu and add header class
        this.mobileMenuDialog?.classList.toggle('mobile-menu--open', open);
        this.mobileMenuHeader?.classList.toggle('mobile-menu-header--open', open);

        if (open) {
            // Open
            this.focusFirstElement();
        } else {
            // Close
            this.menuOpenButton?.focus();
        }
    }

    /**
     * Given a button, it opens or closes its submenu, along with updating aria attributes.
     */
    toggleSubmenu(button: HTMLElement, open: boolean|null = null) {
        const submenu = button.closest('li')?.querySelector<HTMLElement>('.sub-menu');

        if (submenu) {
            open = open != null ? open : !submenu.classList.contains('sub-menu--open');
            if (open) {
                submenu.classList.add('sub-menu--open');
                submenu.setAttribute('aria-hidden', 'false');
                button.setAttribute('aria-expanded', 'true');
            } else {
                // Close
                submenu.classList.remove('sub-menu--open');
                submenu.setAttribute('aria-hidden', 'true');
                button.setAttribute('aria-expanded', 'false');
            }
        }
    }

    /**
     * Focus on first element in dialog
     */
    private focusFirstElement() {
        this.mobileMenuDialog?.querySelector<HTMLInputElement>('.first-focusable-element')?.focus();
    }

    /**
     * Trap focus into the dialog
     */
    private trapFocus(event: KeyboardEvent) {
        const focusableElements = event.currentTarget instanceof HTMLElement ? event.currentTarget
	        .querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])') : false;
        if (!focusableElements) {
	        return;
        }

        if (!event.shiftKey && event.key == 'Tab' && event.target === focusableElements[focusableElements.length - 1]) {
            (focusableElements[0] as HTMLInputElement).focus();
            event.preventDefault();
        } else if (event.shiftKey && event.key == 'Tab' && event.target === focusableElements[0]) {
            (focusableElements[focusableElements.length - 1] as HTMLInputElement).focus();
            event.preventDefault();
        }
    }

    /**
     * Close all submenus
     */
    closeSubMenus() {
        document.querySelectorAll<HTMLElement>('.mobile-menu-item--button').forEach(button => {
            this.toggleSubmenu(button, false);
        });
    }
}

// Setup all menus on the page
for (const wrapper of document.querySelectorAll<HTMLElement>('.mobile-menu-wrapper')) {
    new Menu(wrapper);
}
